/**
 * Contratos del Studio (contexto de soporte, ADR-7). Sobre el cable las
 * fechas van en ISO; los clientes las parsean. Lo marcado «fase dev» expone
 * contenido de las casas (§5: el equipo ve metadatos) y se gatea antes de
 * clientes reales — el contrato lo hace visible, no lo esconde.
 */
import { z } from "zod";
import { TurnTrace } from "./despacho.js";

export const Principal = z.object({
  oid: z.string(),
  name: z.string(),
  email: z.string(),
  scopes: z.array(z.string()),
  roles: z.array(z.string()),
});
/** `via: "demo"` ya no lo emite la API (el Studio solo entra con Entra, ADR-16); se conserva por compatibilidad de lectura. */
/**
 * `rol` (ADR-14 pt 6): `completo` = Studio.Curator o Studio.Admin; `biblioteca`
 * = solo Library.Sign; `ninguno` = sin rol de aplicación (el Studio pinta
 * «sin acceso»). Opcional para leer respuestas de APIs anteriores.
 */
export const StudioRol = z.enum(["completo", "biblioteca", "ninguno"]);
export const Me = z.object({ principal: Principal.nullable(), via: z.enum(["entra", "demo"]), rol: StudioRol.optional() });

export const StudioSkill = z.object({
  slug: z.string(),
  nombre: z.string(),
  capa: z.string(),
  estado: z.enum(["published", "draft", "retired"]),
  version: z.number().int(),
  tema: z.string(),
  /** publicada con un draft esperando encima */
  hasDraft: z.boolean(),
});
export const StudioSkills = z.array(StudioSkill);

/** La casa tal como la ve el equipo (§5: metadatos operativos, nunca contenido).
 *  Las casas del walking skeleton (sin fila en `engagement`) llevan los estructurales a null. */
export const EngagementSummary = z.object({
  engagementId: z.string(),
  companyName: z.string().nullable(),
  sector: z.string().nullable(),
  flavor: z.string().nullable(),
  /** Directivos de la casa y cuántos administran; nunca sus nombres ni correos (§5). */
  executives: z.number().int(),
  admins: z.number().int(),
  createdAt: z.string().datetime().nullable(),
  criterios: z.number().int(),
  pendientesDeConfirmar: z.number().int(),
  decisiones: z.number().int(),
  followUpsAbiertos: z.number().int(),
  episodios: z.number().int(),
  ultimaActividad: z.string().datetime().nullable(),
});
export const EngagementSummaries = z.array(EngagementSummary);

/**
 * El puente de un episodio en el Studio (ADR-21 pt 2): el texto, su fecha,
 * su tramo y el modelo que lo escribió con lo que consumió (tokens; el
 * dinero se valora al leer, como la traza).
 */
export const PuenteEpisodio = z.object({
  texto: z.string(),
  fecha: z.string().datetime(),
  desde: z.string().datetime(),
  hasta: z.string().datetime(),
  modelo: z.string().nullable(),
  tokensEntrada: z.number().int().nullable(),
  tokensSalida: z.number().int().nullable(),
  escritoEn: z.string().datetime(),
});
export type PuenteEpisodio = z.infer<typeof PuenteEpisodio>;

export const EpisodeSummary = z.object({
  conversationId: z.string(),
  turnos: z.number().int(),
  desde: z.string().datetime(),
  hasta: z.string().datetime(),
  /**
   * ADITIVO (2026-11-03, ADR-21): `open` | `closed`. Las conversaciones
   * anteriores a ADR-21 (sin fila de episodio) salen `closed`, sin puente.
   */
  estado: z.enum(["open", "closed"]).optional(),
  /** ADITIVO (2026-11-03, ADR-21 pt 2): el puente, si ya se escribió. */
  puente: PuenteEpisodio.nullable().optional(),
  /** ADITIVO (2026-11-03, ADR-21; M5 paso 3b): una conversación anterior a los episodios (sin fila propia): cerrada y sin puente por construcción. */
  legado: z.boolean().optional(),
});

/** INSTRUMENTO DE FASE DEV: expone contenido de la casa (§5 concede al
 * equipo metadatos) — se gatea antes de clientes reales. Formas reales
 * del dominio; passthrough para no romper con campos nuevos. */
export const Fact = z.object({
  id: z.string(),
  statement: z.string(),
  state: z.string(),
  proposedAt: z.string().datetime(),
  resolvedAt: z.string().datetime().optional(),
  resolvedBy: z.string().optional(),
  /** ADITIVO (2026-11-03, ADR-21 pt 6): de dónde nació — `signup`, `conversation`, `correction` o `document`. */
  origin: z.string().optional(),
}).passthrough();
export const Decision = z.object({
  id: z.string(),
  statement: z.string(),
  decidedBy: z.string(),
  at: z.string().datetime(),
  source: z.object({ conversationId: z.string() }).passthrough(),
}).passthrough();
export const Seguimiento = z.object({
  id: z.string(),
  statement: z.string(),
  openedBy: z.string(),
  at: z.string().datetime(),
  state: z.enum(["open", "resolved"]),
}).passthrough();
export const Memoria = z.object({
  perfil: z.array(z.object({ chainId: z.string(), head: Fact.nullable(), pending: Fact.nullable(), trail: z.array(z.unknown()) })),
  decisiones: z.array(Decision),
  pendientes: z.array(Seguimiento),
  episodios: z.array(EpisodeSummary),
});
export type Fact = z.infer<typeof Fact>;
export type Decision = z.infer<typeof Decision>;
export type Seguimiento = z.infer<typeof Seguimiento>;
/** Un turno del episodio; los del asesor llevan su traza («no es magia», el explain de M2) o null si es anterior a la traza. */
export const Turn = z.object({ role: z.string(), content: z.string(), at: z.string().datetime(), trace: TurnTrace.nullable().optional() });
export const Turns = z.array(Turn);

export type Principal = z.infer<typeof Principal>;
export type Me = z.infer<typeof Me>;
export type StudioSkill = z.infer<typeof StudioSkill>;
export type EngagementSummary = z.infer<typeof EngagementSummary>;
export type EpisodeSummary = z.infer<typeof EpisodeSummary>;
export type Memoria = z.infer<typeof Memoria>;
export type Turn = z.infer<typeof Turn>;

/** Detalle del skill (el banco): versiones reales + método por versión. */
export const SkillVersionInfo = z.object({
  version: z.number().int(),
  estado: z.enum(["draft", "published", "retired"]),
  publishedAt: z.string().datetime().nullable(),
});
export const SkillDetail = z.object({
  slug: z.string(),
  nombre: z.string(),
  tema: z.string(),
  capa: z.string(),
  estado: z.enum(["published", "draft", "retired"]),
  hasDraft: z.boolean(),
  versiones: z.array(SkillVersionInfo),
  /** cuerpo del método por versión (clave = número en string) */
  metodos: z.record(z.string(), z.string()),
});
/**
 * El probador de enrutado: la regla de producción (ADR-20 pt 9), con su
 * traza. `keywords` lleva lo casado ya legible (palabra con su peso,
 * frase con su bono); `score` dejó de ser entero (peso por rareza, a
 * centésimas). ADITIVO (2026-10-26): `tokens` y `phrases`, lo mismo
 * estructurado.
 */
export const EnrutadoRow = z.object({
  slug: z.string(),
  capa: z.string(),
  keywords: z.array(z.string()),
  score: z.number(),
  aplica: z.boolean(),
  tokens: z.array(z.object({ token: z.string(), weight: z.number() })).optional(),
  phrases: z.array(z.object({ phrase: z.string(), source: z.enum(["title", "topic", "guion", "remission", "body"]), bonus: z.number(), whole: z.boolean().optional(), anchored: z.boolean().optional() })).optional(),
});
/**
 * `ganador`: el primer candidato de la regla (null sin candidatos).
 * ADITIVO (2026-10-26, ADR-20 pt 9 reescrito): la regla propone y el
 * modelo elige — `candidatos` (slugs que se le darían al modelo),
 * `strongSingle` (basta la regla) y `nota` («elegiría el modelo entre …»).
 */
export const Enrutado = z.object({
  rows: z.array(EnrutadoRow),
  ganador: z.string().nullable(),
  candidatos: z.array(z.string()).optional(),
  strongSingle: z.boolean().optional(),
  nota: z.string().optional(),
});
export type SkillVersionInfo = z.infer<typeof SkillVersionInfo>;
export type SkillDetail = z.infer<typeof SkillDetail>;
export type EnrutadoRow = z.infer<typeof EnrutadoRow>;
export type Enrutado = z.infer<typeof Enrutado>;

/** Telemetría REAL por Skill, calculada desde las trazas persistidas de cada
 *  respuesta (30 días). Lo que no se puede medir hoy va a null, nunca inventado. */
export const SkillTelemetryRow = z.object({
  slug: z.string(),
  /** Casas distintas en las que el método se aplicó en la ventana. */
  casas: z.number().int(),
  /** Respuestas del asesor con este método en la ventana. */
  disparos: z.number().int(),
  /** Diferencia de disparos frente a la ventana anterior (30–60 días); null sin ventana previa. */
  delta: z.number().int().nullable(),
  /** Propuestas de criterio nacidas en respuestas con este método, y % confirmadas entre las resueltas (null si ninguna resuelta). */
  propuestas: z.number().int(),
  confirmadasPct: z.number().int().nullable(),
  ultimoUso: z.string().datetime().nullable(),
});
export const SkillsTelemetry = z.object({
  desde: z.string().datetime(),
  hasta: z.string().datetime(),
  porSkill: z.array(SkillTelemetryRow),
  /** Respuestas sin método aplicable en la ventana (excluida la apertura de la primera vez): lo que falta por escribir. */
  sinMetodo: z.number().int(),
  totalRespuestas: z.number().int(),
});
export type SkillTelemetryRow = z.infer<typeof SkillTelemetryRow>;
export type SkillsTelemetry = z.infer<typeof SkillsTelemetry>;

/** Rutas del Studio — el mapa único que comparten API y cliente. */
export const STUDIO_ROUTES = {
  me: "/studio/me",
  skills: "/studio/skills",
  skillsTelemetria: "/studio/skills/telemetria",
  skill: (slug: string) => `/studio/skills/${encodeURIComponent(slug)}`,
  enrutado: (frase: string) => `/studio/enrutado?q=${encodeURIComponent(frase)}`,
  engagements: "/studio/engagements",
  memoria: (engagementId: string) => `/studio/engagements/${encodeURIComponent(engagementId)}/memoria`,
  episodio: (engagementId: string, conversationId: string) => `/studio/engagements/${encodeURIComponent(engagementId)}/episodios/${encodeURIComponent(conversationId)}`,
} as const;
